/**
 * Game, open run and team practice. Everything inside #gameRoot, talking to
 * /api/game.
 *
 * Five screens, one module: the list of runs, the setup, the live sideline
 * sheet, the card a run settles into, and the playback where the recording
 * plays with its own timeline running beside it.
 *
 * THE TIMELINE IS THE POINT. The moment the recording starts, t0 is taken.
 * Every tap and every measured rep carries t, milliseconds from t0, and the
 * Worker stores t and derives everything else from it. On the playback screen
 * the video's currentTime is the same clock, so an event logged at 4 minutes 12
 * appears at 4 minutes 12, and clicking it seeks there. That is the feature: a
 * player watching their run back sees what happened, when it happened, with the
 * read beside it.
 *
 * WHAT IS MEASURED AND WHAT IS LOGGED, on every number, on every screen.
 * The camera is MediaPipe pose. 33 body points. It cannot see the ball, the
 * hoop, the score or who anybody is. So:
 *   MEASURED (blue)   posture and movement. Stance, balance, dribble, drive,
 *                     box out shape, shooting form, and how those drift across
 *                     the run compared to this player's own first reps.
 *   LOGGED (purple)   every make, miss, rebound, assist, steal, block,
 *                     turnover, box out, fast break. Somebody tapped it.
 *   SELF (purple)     how the run felt, because they were asked.
 * There is no detector here for body language, faces or eyes, and there is not
 * going to be one. If a screen needs to know how a run felt, it asks.
 *
 * There is also no overall rating of a player on this screen. worker/ratings.js
 * settled that: a single score on a kid reads as a scouting grade. What this
 * screen gamifies is the RUN, with per category numbers that each say where
 * they came from.
 */

import { elementFor } from '../scoring.js';
import { createTrackClient } from './trackclient.js';
import * as vault from './vault.js';

const VISION_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* ------------------------------------------------------------------ shapes */

/** The sideline sheet, in tap order. One row per group, biggest targets first. */
const SHEET = [
  { group: 'Shooting', keys: ['fg2_made', 'fg2_miss', 'fg3_made', 'fg3_miss', 'ft_made', 'ft_miss'] },
  { group: 'Boards', keys: ['rebound_off', 'rebound_def', 'boxout'] },
  { group: 'Playmaking', keys: ['assist', 'turnover', 'screen'] },
  { group: 'Defense', keys: ['steal', 'block', 'foul'] },
  { group: 'Transition', keys: ['transition_back', 'fastbreak'] },
  { group: 'Effort', keys: ['hustle'] },
];

/** Short faces for the big buttons. The long labels come from the Worker. */
const SHORT = {
  fg2_made: '2PT<br><b>MAKE</b>', fg2_miss: '2PT<br><b>MISS</b>',
  fg3_made: '3PT<br><b>MAKE</b>', fg3_miss: '3PT<br><b>MISS</b>',
  ft_made: 'FT<br><b>MAKE</b>', ft_miss: 'FT<br><b>MISS</b>',
  rebound_off: 'OFF<br><b>REB</b>', rebound_def: 'DEF<br><b>REB</b>', boxout: '<b>BOX<br>OUT</b>',
  assist: '<b>ASSIST</b>', turnover: '<b>TURN<br>OVER</b>', screen: '<b>SCREEN</b>',
  steal: '<b>STEAL</b>', block: '<b>BLOCK</b>', foul: '<b>FOUL</b>',
  transition_back: '<b>GOT<br>BACK</b>', fastbreak: '<b>FAST<br>BREAK</b>',
  hustle: '<b>HUSTLE</b>',
};
const GOOD = new Set(['fg2_made', 'fg3_made', 'ft_made', 'rebound_off', 'rebound_def', 'boxout', 'assist', 'steal', 'block', 'screen', 'transition_back', 'fastbreak', 'hustle']);
const BADKEY = new Set(['fg2_miss', 'fg3_miss', 'ft_miss', 'turnover', 'foul']);

/** What a spoken line has to contain to count as that event. First match wins. */
const PHRASES = [
  // At the rim first: "dunked it" and "laid it in" are made shots, and the
  // bare made/miss rules at the bottom would otherwise swallow them.
  [/\b(dunk(ed)?|jam(med)?|threw it down|poster(ised|ized)?)\b.*\b(miss|missed|off|blocked)\b|\bmiss(ed)?\b.*\bdunk\b/, 'dunk_miss'],
  [/\b(dunk(ed)?|jam(med)?|threw it down|poster(ised|ized)?|slam)\b/, 'dunk_made'],
  [/\b(layup|lay up|lay-up|floater|finger roll|reverse|scoop|put ?back)\b.*\b(miss|missed|off|short|blocked|no good)\b|\bmiss(ed)?\b.*\b(layup|lay up|floater)\b/, 'layup_miss'],
  [/\b(layup|lay up|lay-up|floater|finger roll|reverse|scoop|put ?back|laid it in)\b/, 'layup_made'],
  [/\b(three|3)\b.*\b(made|make|good|in|bucket|splash|cash)\b|\b(made|make|knock(ed)? down|hit)\b.*\b(three|3)\b/, 'fg3_made'],
  [/\b(three|3)\b.*\b(miss|missed|short|off|brick|no good)\b|\bmiss(ed)?\b.*\b(three|3)\b/, 'fg3_miss'],
  [/\bfree throw\b.*\b(made|make|good|in)\b|\b(made|make)\b.*\bfree throw\b/, 'ft_made'],
  [/\bfree throw\b.*\b(miss|missed|off|short)\b|\bmiss(ed)?\b.*\bfree throw\b/, 'ft_miss'],
  [/\b(offensive|off)\b.*\bre?bound\b/, 'rebound_off'],
  [/\b(defensive|def)\b.*\bre?bound\b/, 'rebound_def'],
  [/\bre?bound\b/, 'rebound_def'],
  [/\bbox(ed)?\s*out\b|\bboxout\b/, 'boxout'],
  [/\bassist\b|\bdime\b/, 'assist'],
  [/\bturn\s*over\b|\bturnover\b|\blost it\b/, 'turnover'],
  [/\bscreen\b|\bpick\b/, 'screen'],
  [/\bsteal\b|\bpicked it\b/, 'steal'],
  [/\bblock(ed)?\b|\bswat\b/, 'block'],
  [/\bfoul\b/, 'foul'],
  [/\bgot back\b|\bback on d\b|\bback on defen[cs]e\b|\btransition d\b/, 'transition_back'],
  [/\bfast break\b|\bfastbreak\b|\bbreak\b/, 'fastbreak'],
  [/\bhustle\b|\bdove\b|\bfloor burn\b|\bloose ball\b/, 'hustle'],
  [/\bclutch (time )?(on|start)\b|\bcrunch time\b/, 'clutch_on'],
  [/\bclutch (time )?(off|over|done)\b/, 'clutch_off'],
  // Two and the bare "made" and "missed" go last: a three is still a made shot.
  [/\b(made|make|good|bucket)\b/, 'fg2_made'],
  [/\b(miss|missed|off|short|brick)\b/, 'fg2_miss'],
];

/**
 * Handles, matched separately from the shot so "step back three, good" logs
 * both the move and the three. Naming the move is the player's call: the
 * camera can measure that a dribble happened and how low it was, not what it
 * was called.
 */
const MOVE_PHRASES = [
  [/\b(cross(ed)?( ?over)?|crossover|shamgod)\b/, 'move_crossover'],
  [/\bbetween the legs\b|\bthrough the legs\b/, 'move_between'],
  [/\bbehind the back\b|\bbehind my back\b|\bwrap ?around\b/, 'move_behind'],
  [/\bspin( move)?\b|\bspun\b/, 'move_spin'],
  [/\bhesi(tation)?\b|\bhesy\b|\bstutter\b/, 'move_hesi'],
  [/\bstep ?back\b|\bside ?step\b|\bpull ?back\b/, 'move_stepback'],
];

/** Every logged type that is an attempt, so a spot can ride along with it. */
const SHOT_TYPES = new Set(['fg2_made','fg2_miss','fg3_made','fg3_miss','ft_made','ft_miss',
  'layup_made','layup_miss','dunk_made','dunk_miss']);

/**
 * Where they are standing. Matched on its own pass so "corner three, good"
 * logs a made three AND remembers the corner. The spot sticks until they say
 * another one, which is how somebody actually talks while they are shooting.
 * The camera has no idea where the hoop is: a spot is never measured.
 */
const SPOT_PHRASES = [
  [/\bcorner( three| 3)?\b/, 'corner3'],
  [/\b(wing|slot)( three| 3)?\b/, 'wing3'],
  [/\b(top of the key|top of key|straight ?away)( three| 3)?\b/, 'top3'],
  [/\b(deep|logo|way out|from the parking lot)\b/, 'deep'],
  [/\b(free ?throw|foul) line\b|\bcharity stripe\b/, 'free-throw'],
  [/\belbow\b/, 'elbow'],
  [/\b(mid ?range|midrange|pull ?up spot)\b/, 'mid'],
  [/\b(short (mid|corner)|short range|close range)\b/, 'short-mid'],
  [/\b(in the paint|paint|block|post|key)\b/, 'paint'],
  [/\b(at the rim|under the (basket|rim)|point ?blank)\b/, 'rim'],
];

/* ------------------------------------------------------------------ helpers */

const n0 = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const d2 = (n) => String(n).padStart(2, '0');
/** A timeline position, as a clock. This is the format the whole screen uses. */
const clock = (ms) => {
  const s = Math.max(0, Math.round(n0(ms) / 1000));
  return `${Math.floor(s / 60)}:${d2(s % 60)}`;
};
const pctText = (v) => (v === null || v === undefined ? 'no attempts' : `${Math.round(v)}%`);
const fmtDay = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''; };
const rid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/** The two tags. Cool is what the app measured, grape is what a person said. */
const tag = (source) => {
  if (source === 'measured') return '<i class="gmtag measured">Measured</i>';
  if (source === 'self') return '<i class="gmtag logged">Self reported</i>';
  return '<i class="gmtag logged">Logged</i>';
};

/* ------------------------------------------------------------------- state */

let ctx = null, root = null, body = null;
const S = {
  screen: 'list',          // list | setup | live | card | play
  cfg: null,               // kinds, logged, markers, measured, copy, from the Worker
  runs: [],
  err: null,
  people: null,            // who this account may link, from /api/game/people
  setup: null,             // the run being built
  run: null,               // the live or opened run
  live: null,              // the card the Worker sends back with every flush
  wantCam: false,          // the run asked for a camera or a recording
  slots: [],               // pose layer body ids, in the order they first appeared
  jersey: {},              // per slot: how the jersey read is going (see lockByJersey)
  tape: [],                // every event this client has logged, newest last
  counts: {},              // per player counts, so the HUD does not wait on a flush
  pending: [],             // logged, not yet flushed
  flushing: false,
  flushTimer: null,
  who: null,               // the roster id the next tap belongs to
  clutch: false,
  t0: 0,                   // performance.now() at the first frame of the recording
  ticking: null,
  cam: { on: false, stream: null, client: null, raf: 0, recorder: null, tracker: null, poseErr: null, bodies: 0 },
  play: null,              // { run, url, events, i }
};

const kindLabel = (id) => S.cfg?.kinds?.find((k) => k.id === id)?.label || id;
const spotName = (id) => S.cfg?.spots?.[id] || id;
const typeLabel = (t) => S.cfg?.logged?.[t]?.label || S.cfg?.markers?.[t]?.label || S.cfg?.measured?.[t]?.label || t;

/* -------------------------------------------------------------------- boot */

export function mount(c) {
  ctx = c;
  root = document.getElementById('gameRoot');
  if (!root) return;
  root.innerHTML = '<div class="gmwrap" id="gmBody"></div>';
  body = root.querySelector('#gmBody');
  // One delegated listener for the whole screen. The sideline sheet repaints
  // constantly and a per button handler would leak one every repaint.
  body.addEventListener('click', onClick);
  body.addEventListener('change', onChange);
  paint();
}

export async function render() {
  if (!root) return;
  if (S.screen === 'live' || S.screen === 'play') return;   // never repaint over a run in progress
  try {
    const data = await ctx.api('/api/game');
    S.cfg = { kinds: data.kinds, logged: data.logged, markers: data.markers, measured: data.measured, copy: data.copy };
    S.runs = Array.isArray(data.runs) ? data.runs : [];
    S.err = null;
  } catch (e) { S.err = e.message; }
  paint();
}

/* ------------------------------------------------------------------ router */

function paint() {
  if (!body) return;
  try {
    if (S.screen === 'setup') body.innerHTML = setupView();
    else if (S.screen === 'live') body.innerHTML = liveView();
    else if (S.screen === 'card') body.innerHTML = cardView();
    else if (S.screen === 'play') body.innerHTML = playView();
    else body.innerHTML = listView();
    after();
  } catch (e) {
    console.error('game screen failed', e);
    body.innerHTML = `<div class="card"><h3>Runs</h3><p class="gmsub">This screen broke on the way in. ${esc(e.message)}</p></div>`;
  }
}

/** Anything that has to happen after the markup exists. */
function after() {
  if (S.screen === 'live' && S.cam.on) attachStage();
  if (S.screen === 'play') attachPlayer();
}

/* -------------------------------------------------------------------- list */

function listView() {
  const copy = S.cfg?.copy || {};
  const rows = S.runs.length ? S.runs.map(runRow).join('') :
    '<p class="gmsub">No runs yet. Start one when you get to the gym.</p>';
  return `
    <div class="vhead">
      <div><h1>Runs</h1><p>Games, open runs and team practice. The camera scores your form, you tap the rest.</p></div>
      <div class="right"><button class="btn primary" data-act="new">Start a run</button></div>
    </div>
    ${S.err ? `<div class="card gmerr"><p>${esc(S.err)}</p></div>` : ''}
    <div class="card gmhonest">
      <h3>What this screen can and cannot see</h3>
      <p>${esc(copy.camera || 'The camera sees body points, not the ball. Every make, miss and rebound is logged by a person.')}</p>
      <p>${esc(copy.feelings || 'This app does not read faces, eyes or body language. If it matters how a run felt, it asks.')}</p>
    </div>
    <div class="gmlist">${rows}</div>`;
}

function runRow(r) {
  const me = (r.card?.players || []).find((p) => (r.trackedIds || []).includes(p.playerId)) || r.card?.players?.[0];
  const b = me?.box;
  const tiles = b ? `
    <div class="gmmini">
      <span><b>${n0(b.points)}</b>pts</span>
      <span><b>${pctText(b.fg2.pct)}</b>2PT</span>
      <span><b>${pctText(b.fg3.pct)}</b>3PT</span>
      <span><b>${n0(b.rebounds.total)}</b>reb</span>
      <span><b>${n0(b.boxouts)}</b>box outs</span>
    </div>` : '<p class="gmsub">Nothing logged in this one.</p>';
  return `
    <div class="card gmrow" data-act="open" data-id="${esc(r.id)}">
      <div class="gmrowhead">
        <div>
          <b>${esc(kindLabel(r.kind))}${r.title ? `: ${esc(r.title)}` : ''}</b>
          <small>${esc(fmtDay(r.startedAt))} &middot; ${r.events} events${r.durationMs ? ` &middot; ${clock(r.durationMs)}` : ''}${r.filedBy ? ` &middot; filed by ${esc(r.filedBy.orgName)}` : ''}</small>
        </div>
        ${r.clip ? '<span class="pill cool">Recorded</span>' : ''}
      </div>
      ${tiles}
      ${b ? `<div class="gmrowtags">${tag('logged')}${me.measured?.has ? tag('measured') : ''}</div>` : ''}
    </div>`;
}

/* ------------------------------------------------------------------- setup */

function blankSetup() {
  return {
    kind: 'run',
    title: '',
    roster: [{ id: rid('r'), label: ctx?.S?.settings?.displayName || 'Me', accountId: null, tracked: true }],
    record: true,
    camera: true,
  };
}

function setupView() {
  const s = S.setup;
  const kinds = (S.cfg?.kinds || []).map((k) => `
    <button class="gmkind${s.kind === k.id ? ' on' : ''}" data-act="kind" data-id="${esc(k.id)}">
      <b>${esc(k.label)}</b><small>${esc(k.blurb)}</small>
    </button>`).join('');
  const canLink = Array.isArray(S.people?.people) && S.people.people.length > 0;
  const rows = s.roster.map((p, i) => `
    <div class="gmrost" data-i="${i}">
      <input class="gmin" data-act="label" data-i="${i}" value="${esc(p.label)}" placeholder="Name on the sheet" maxlength="40">
      <input class="gmin gmnum" data-act="number" data-i="${i}" value="${esc(p.number || '')}" placeholder="#" maxlength="2" inputmode="numeric" title="Jersey number. With a number the camera can tell who is who by the shirt, never by the face.">
      ${canLink ? `<select class="gmin gmsel" data-act="link" data-i="${i}">
        <option value="">Not linked</option>
        ${S.people.people.map((x) => `<option value="${esc(x.accountId)}"${p.accountId === x.accountId ? ' selected' : ''}>${esc(x.label)}${x.minor ? ' (under 18)' : ''} &middot; ${esc(x.orgName)}</option>`).join('')}
      </select>` : ''}
      <button class="gmtrack${p.tracked ? ' on' : ''}" data-act="track" data-i="${i}" title="The camera scores this player's form">Camera</button>
      ${s.roster.length > 1 ? `<button class="gmx" data-act="drop" data-i="${i}" aria-label="Take off the roster">&times;</button>` : ''}
    </div>`).join('');
  const linked = s.roster.filter((p) => p.accountId);
  const filing = linked.map((p) => {
    const person = S.people?.people?.find((x) => x.accountId === p.accountId);
    if (!person) return '';
    return `<li>${esc(person.label)}: ${person.filesToThem
      ? 'this run files onto their own record, because they share their sessions with ' + esc(person.orgName) + '.'
      : 'this run stays on your copy. They have not shared their sessions with ' + esc(person.orgName) + ', so nothing is written to their record.'}</li>`;
  }).join('');

  return `
    <div class="vhead">
      <div><h1>Start a run</h1><p>Who is in it, and who the camera watches.</p></div>
      <div class="right"><button class="btn" data-act="cancel">Back</button></div>
    </div>
    ${S.err ? `<div class="card gmerr"><p>${esc(S.err)}</p></div>` : ''}
    <div class="card">
      <h3>What kind</h3>
      <div class="gmkinds">${kinds}</div>
      <input class="gmin gmtitle" data-act="title" value="${esc(s.title)}" placeholder="Call it something, or leave it blank" maxlength="80">
    </div>
    <div class="card">
      <h3>Who is playing</h3>
      <div class="gmrosts">${rows}</div>
      <div class="gmrow2">
        <button class="btn" data-act="add">Add a player</button>
        ${canLink ? '' : `<small class="gmsub">${esc(S.people?.reason || 'Sign in and join a family or team to link a Hooper Buddy account.')}</small>`}
      </div>
      ${filing ? `<ul class="gmnote">${filing}</ul>` : ''}
      <p class="gmsub">A name with nobody attached is just a name on your sheet. Nothing is written to anybody else's record for it.</p>
    </div>
    <div class="card">
      <h3>The camera</h3>
      <div class="srow"><div class="lab"><b>Score my form</b><small>Stance, balance, drive and shooting shape on the players marked Camera. Posture only. The camera never sees the ball.</small></div><div class="sw${s.camera ? ' on' : ''}" data-act="cam"></div></div>
      <div class="srow"><div class="lab"><b>Record the run</b><small>Saved to this device, in your Vault, so you can watch it back with the timeline. Nothing uploads unless you choose to.</small></div><div class="sw${s.record ? ' on' : ''}" data-act="rec"></div></div>
    </div>
    <button class="btn primary lg btn-wide gmgo" data-act="start">Start ${esc(kindLabel(s.kind)).toLowerCase()}</button>`;
}

/* -------------------------------------------------------------------- live */

function liveView() {
  const run = S.run;
  const multi = run.roster.length > 1;
  const who = S.who || run.roster[0]?.id;
  const strip = multi ? `<div class="gmwho">${run.roster.map((p) => `
    <button class="gmwhob${p.id === who ? ' on' : ''}" data-act="who" data-id="${esc(p.id)}">
      ${p.number ? `<span class="gmjersey">#${esc(p.number)}</span> ` : ''}${esc(p.label)}${run.trackedIds.includes(p.id) ? '<i class="gmcam" aria-hidden="true"></i>' : ''}
    </button>`).join('')}</div>` : '';

  const sheet = SHEET.map((g) => `
    <div class="gmgrp">
      <h4>${esc(g.group)}</h4>
      <div class="gmkeys">${g.keys.map((k) => `
        <button class="gmkey${GOOD.has(k) ? ' good' : BADKEY.has(k) ? ' bad' : ''}" data-act="tap" data-key="${esc(k)}" title="${esc(typeLabel(k))}">${SHORT[k] || esc(typeLabel(k))}</button>`).join('')}</div>
    </div>`).join('');

  const hud = hudFor(who);
  const last = S.tape.slice(-6).reverse();
  // A run with no camera does not get a camera shaped hole. On a phone that
  // box is most of the screen, and the sheet is the thing somebody on a
  // sideline actually needs under their thumb.
  const failed = S.wantCam && !S.cam.on && S.cam.poseErr;
  const stage = !S.wantCam ? ''
    : failed ? `<div class="card gmcamoff"><h3>The camera</h3><p>${esc(S.cam.poseErr)}</p></div>`
    : `
        <div class="stage gmvid">
          <video id="gmCam" playsinline muted></video>
          <canvas id="gmOverlay"></canvas>
          ${S.cam.recorder ? '<div class="rec" id="gmRec"><i></i>REC</div>' : ''}
          ${!S.cam.on ? '<div class="gmoff"><p>Starting the camera.</p></div>' : ''}
        </div>`;

  return `
    <div class="vhead">
      <div><h1>${esc(kindLabel(run.kind))}${run.title ? `: ${esc(run.title)}` : ''}</h1><p id="gmClock" class="gmclock">0:00</p></div>
      <div class="right">
        <button class="btn" data-act="mic">Voice</button>
        <button class="btn primary" data-act="end">End run</button>
      </div>
    </div>
    <div class="gmlive">
      <div class="gmstage">
        ${stage}
        <div class="card gmhud">
          <h3>${esc(run.roster.find((p) => p.id === who)?.label || 'This run')} ${tag('logged')}</h3>
          <div class="grid-stats">
            <div class="stat"><b>${hud.points}</b><small>points</small></div>
            <div class="stat"><b>${hud.fg2}</b><small>2PT</small></div>
            <div class="stat"><b>${hud.fg3}</b><small>3PT</small></div>
            <div class="stat"><b>${hud.ft}</b><small>FT</small></div>
            <div class="stat"><b>${hud.reb}</b><small>rebounds</small></div>
            <div class="stat"><b>${hud.box}</b><small>box outs</small></div>
          </div>
          ${measuredStrip(who)}
        </div>
      </div>
      <div class="gmsheet">
        ${strip}
        <div class="gmbar">
          <button class="gmclutch${S.clutch ? ' on' : ''}" data-act="clutch">${S.clutch ? 'Clutch time ON' : 'Mark clutch time'}</button>
          <button class="gmundo" data-act="undo"${S.tape.length ? '' : ' disabled'}>Undo</button>
        </div>
        ${sheet}
        <div class="gmtape">${last.length ? last.map((e) => `
          <div class="gmtapel"><span>${clock(e.t)}</span>${esc(typeLabel(e.type))}${e.playerId && multi ? ` &middot; ${esc(run.roster.find((p) => p.id === e.playerId)?.label || '')}` : ''}</div>`).join('')
          : '<div class="gmtapel gmsub">Every tap lands here, newest first.</div>'}</div>
        <p class="gmsub">${esc(S.cfg?.copy?.pressure || '')}</p>
      </div>
    </div>`;
}

/** The few numbers the sideline needs instantly, from the local tally. */
function hudFor(playerId) {
  const c = S.counts[playerId] || {};
  const g = (k) => n0(c[k]);
  return {
    points: g('fg2_made') * 2 + g('fg3_made') * 3 + g('ft_made'),
    fg2: `${g('fg2_made')}/${g('fg2_made') + g('fg2_miss')}`,
    fg3: `${g('fg3_made')}/${g('fg3_made') + g('fg3_miss')}`,
    ft: `${g('ft_made')}/${g('ft_made') + g('ft_miss')}`,
    reb: g('rebound_off') + g('rebound_def'),
    box: g('boxout'),
  };
}

/** The measured side of the live panel, only when the camera actually scored something. */
function measuredStrip(playerId) {
  const card = S.live?.players?.find((p) => p.playerId === playerId);
  const kinds = Object.entries(card?.measured?.kinds || {});
  // When the pose layer is not in this build only shooting form is measured.
  // Say that, rather than leaving a player to read an empty panel as "my
  // stance was not good enough to score".
  const partial = S.cam.tracker?.partial ? '<p class="gmsub">Only shooting form is being measured in this build. Stance, balance, dribble and drives need the pose panel.</p>' : '';
  if (!kinds.length) return `<p class="gmsub">${esc(S.cam.on ? 'The camera has not scored a rep yet.' : 'No form measured in this run.')}</p>${partial}`;
  const rough = card?.measured?.anyUncalibrated ? `<p class="gmsub">These are uncalibrated, so they are a rough guide.</p>` : '';
  return `<div class="gmmeas">${tag('measured')}${kinds.map(([k, v]) => {
    const el = elementFor(n0(v.avg));
    return `<div class="gmmrow"><span>${esc(v.label)}</span><b class="gmel" data-el="${esc(el.id)}">${n0(v.avg)}</b><small>${n0(v.reps)} reps</small></div>`;
  }).join('')}</div>${rough}${partial}`;
}

/* -------------------------------------------------------------------- card */

function cardView() {
  const run = S.run;
  if (!run) return listView();
  const card = run.card;
  const copy = S.cfg?.copy || {};
  const filed = run.filed;
  return `
    <div class="vhead">
      <div><h1>${esc(kindLabel(run.kind))}${run.title ? `: ${esc(run.title)}` : ''}</h1>
        <p>${esc(fmtDay(run.startedAt))} &middot; ${clock(run.durationMs)} &middot; ${n0(card?.counts?.logged)} logged, ${n0(card?.counts?.measured)} measured</p></div>
      <div class="right">
        ${run.clip && !run.trimmed ? '<button class="btn primary" data-act="watch">Watch it back</button>' : ''}
        <button class="btn" data-act="cancel">Back</button>
      </div>
    </div>
    ${run.reportError ? `<div class="card gmerr"><p>${esc(run.reportError)}</p></div>` : ''}
    ${run.trimmed ? `<div class="card"><h3>The timeline of this run</h3><p class="gmsub">The numbers below are the whole card and nothing was lost from them. The moment by moment timeline, all ${n0(run.eventCount)} of it, was dropped to keep your account inside what it can store. Only the most recent runs keep one, so this run cannot be watched back with its events any more.</p></div>` : ''}
    ${(card?.players || []).map((p) => playerBlock(p, run)).join('')}
    ${run.selfCheck ? selfBlock(run.selfCheck) : selfAsk()}
    ${run.report ? `<div class="card"><h3>What your companion made of it</h3><p class="gmreport">${esc(run.report)}</p></div>` : ''}
    ${filed && (filed.filed?.length || filed.skipped?.length) ? `
      <div class="card"><h3>Where this run went</h3>
        ${(filed.filed || []).map((f) => `<p class="gmsub">${esc(f.label)}: filed onto their own record through ${esc(f.orgName)}.</p>`).join('')}
        ${(filed.skipped || []).map((f) => `<p class="gmsub">${esc(f.label)}: ${esc(f.why)}</p>`).join('')}
      </div>` : ''}
    <div class="card gmhonest">
      <h3>Reading this card</h3>
      <p>${esc(copy.logged || '')}</p>
      <p>${esc(copy.measured || '')}</p>
      <p>${esc(copy.drift || '')}</p>
      <p>There is no overall rating on this card and there is not going to be one. One number on a player reads as a grade, and this is a record of one run.</p>
    </div>`;
}

function playerBlock(p, run) {
  const b = p.box;
  const nothing = !Object.keys(b.logged || {}).length && !p.measured.has;
  if (nothing) return '';
  return `
    <div class="card gmplayer">
      <div class="gmphead">
        <h3>${esc(p.label)}</h3>
        ${p.tracked ? '<span class="pill cool">Camera was on them</span>' : ''}
        ${p.accountId ? '<span class="pill">Linked account</span>' : ''}
      </div>
      <div class="gmsect"><h4>Logged courtside ${tag('logged')}</h4>
        <div class="grid-stats">
          <div class="stat"><b>${n0(b.points)}</b><small>points</small></div>
          <div class="stat"><b>${pctText(b.fg2.pct)}</b><small>2PT &middot; ${b.fg2.made}/${b.fg2.att}</small></div>
          <div class="stat"><b>${pctText(b.fg3.pct)}</b><small>3PT &middot; ${b.fg3.made}/${b.fg3.att}</small></div>
          <div class="stat"><b>${pctText(b.ft.pct)}</b><small>FT &middot; ${b.ft.made}/${b.ft.att}</small></div>
          <div class="stat"><b>${pctText(b.efg)}</b><small>effective FG</small></div>
          <div class="stat"><b>${pctText(b.ts)}</b><small>true shooting</small></div>
          <div class="stat"><b>${n0(b.rebounds.total)}</b><small>rebounds &middot; ${n0(b.rebounds.off)} off</small></div>
          <div class="stat"><b>${n0(b.assists)}</b><small>assists</small></div>
          <div class="stat"><b>${n0(b.turnovers)}</b><small>turnovers</small></div>
          <div class="stat"><b>${b.astTo === null ? '&mdash;' : b.astTo}</b><small>assist to turnover</small></div>
          <div class="stat"><b>${n0(b.steals)}</b><small>steals</small></div>
          <div class="stat"><b>${n0(b.blocks)}</b><small>blocks</small></div>
          <div class="stat"><b>${n0(b.boxouts)}</b><small>box outs</small></div>
          <div class="stat"><b>${n0(b.transitionBack)}</b><small>got back</small></div>
          <div class="stat"><b>${n0(b.fastbreaks)}</b><small>fast breaks</small></div>
          <div class="stat"><b>${n0(b.workCounted)}</b><small>work counted</small></div>
        </div>
      </div>
      ${p.measured.has ? measuredBlock(p) : `<div class="gmsect"><h4>Measured by the camera ${tag('measured')}</h4><p class="gmsub">Nothing was measured for ${esc(p.label)} in this run.</p></div>`}
      ${p.clutch ? `
        <div class="gmsect"><h4>Inside the stretches somebody marked clutch ${tag('logged')}</h4>
          <div class="grid-stats">
            <div class="stat"><b>${n0(p.clutch.box.points)}</b><small>points</small></div>
            <div class="stat"><b>${p.clutch.box.fg.made}/${p.clutch.box.fg.att}</b><small>from the floor</small></div>
            <div class="stat"><b>${n0(p.clutch.box.turnovers)}</b><small>turnovers</small></div>
          </div>
          <p class="gmsub">${esc(S.cfg?.copy?.pressure || '')}</p>
        </div>` : ''}
    </div>`;
}

/**
 * How sure a measured score is. A rep is either scored against this player's
 * own measured ranges or against a fixed threshold nobody has checked on a
 * real person, and a number out of a hundred means very different things in
 * those two cases. So the card says which, every time, rather than letting a
 * parent assume the higher one.
 */
function calibNote(v) {
  const cal = n0(v.calibratedReps);
  if (cal >= n0(v.reps) && cal > 0) return { text: 'calibrated to their own reps', rough: false };
  if (cal > 0) return { text: `${cal} of ${n0(v.reps)} reps calibrated`, rough: true };
  return { text: 'not calibrated, a rough guide', rough: true };
}

function measuredBlock(p) {
  const rows = Object.entries(p.measured.kinds).map(([k, v]) => {
    const el = elementFor(n0(v.avg));
    const drift = v.drift === null
      ? 'not enough reps to compare'
      : v.drift === 0 ? 'held level against their own first reps'
      : `${v.drift > 0 ? 'up' : 'down'} ${Math.abs(v.drift)} against their own first reps`;
    const cal = calibNote(v);
    return `
      <div class="gmmcard">
        <div class="gmmtop"><span>${esc(v.label)}</span><b class="gmel" data-el="${esc(el.id)}">${n0(v.avg)}</b></div>
        <div class="bar"><i style="width:${Math.min(100, n0(v.avg))}%;background:${esc(el.color)}"></i></div>
        <small>${n0(v.reps)} reps &middot; best ${n0(v.best)} &middot; ${esc(drift)}</small>
        <small class="gmcal${cal.rough ? ' rough' : ''}">${esc(cal.text)}</small>
      </div>`;
  }).join('');
  return `<div class="gmsect"><h4>Measured by the camera ${tag('measured')}</h4><div class="gmmcards">${rows}</div>
    ${p.measured.anyUncalibrated ? `<p class="gmsub">${esc(S.cfg?.copy?.calibration || '')}</p>` : ''}
    <p class="gmsub">${esc(S.cfg?.copy?.drift || '')}</p></div>`;
}

const SELF = [['legs', 'Legs'], ['focus', 'Focus'], ['energy', 'Energy']];

function selfAsk() {
  return `
    <div class="card">
      <h3>How did it feel ${tag('self')}</h3>
      <p class="gmsub">Nothing on this screen guessed at this. The camera does not read faces or body language, so if it matters, we ask.</p>
      ${SELF.map(([k, label]) => `
        <div class="gmscale"><span>${esc(label)}</span>
          ${[1, 2, 3, 4, 5].map((n) => `<button class="gmdot" data-act="self" data-k="${k}" data-n="${n}">${n}</button>`).join('')}
        </div>`).join('')}
      <input class="gmin" data-act="selfnote" placeholder="Anything worth remembering about it" maxlength="400">
      <button class="btn wide gmgo" data-act="selfsave">Save how it felt</button>
    </div>`;
}

function selfBlock(sc) {
  return `<div class="card"><h3>How it felt ${tag('self')}</h3>
    <div class="grid-stats">${SELF.map(([k, label]) => `<div class="stat"><b>${sc[k] === null || sc[k] === undefined ? '&mdash;' : `${sc[k]}/5`}</b><small>${esc(label.toLowerCase())}</small></div>`).join('')}</div>
    ${sc.note ? `<p class="gmsub">${esc(sc.note)}</p>` : ''}</div>`;
}

/* ---------------------------------------------------------------- playback
 *
 * The recording and the timeline are one clock. video.currentTime * 1000 is t,
 * because t0 was taken on the frame the recorder started.
 */

function playView() {
  const p = S.play;
  if (!p) return listView();
  const run = p.run;
  const dur = n0(run.durationMs) || 1;
  const ticks = p.events.map((e, i) => `
    <button class="gmtick ${e.source}" style="left:${Math.min(99.5, (e.t / dur) * 100)}%"
      data-act="seek" data-i="${i}" title="${esc(typeLabel(e.type))} at ${clock(e.t)}"></button>`).join('');
  return `
    <div class="vhead">
      <div><h1>Watch it back</h1><p>${esc(kindLabel(run.kind))}${run.title ? `: ${esc(run.title)}` : ''} &middot; ${esc(fmtDay(run.startedAt))}</p></div>
      <div class="right"><button class="btn" data-act="tocard">The card</button><button class="btn" data-act="cancel">Back</button></div>
    </div>
    ${p.err ? `<div class="card gmerr"><p>${esc(p.err)}</p></div>` : ''}
    <div class="gmplay">
      <div>
        <div class="stage gmvid"><video id="gmPlay" playsinline controls></video></div>
        <div class="gmtrack2">
          <div class="gmticks">${ticks}<i class="gmhead2" id="gmHead"></i></div>
          <div class="gmlegend"><span class="gmtick logged"></span>Logged by a person<span class="gmtick measured"></span>Measured by the camera</div>
        </div>
        <div class="card gmread"><h3>Right now</h3><p id="gmRead">Press play. Everything that happened shows up on the moment it happened.</p></div>
      </div>
      <div class="card gmfeedcard">
        <h3>The run, as it happened</h3>
        <div class="gmfeed" id="gmFeed"></div>
      </div>
    </div>
    ${run.report ? `<div class="card"><h3>What your companion made of it</h3><p class="gmreport">${esc(run.report)}</p></div>` : ''}`;
}

/**
 * The read for one moment. Built here, from the event and the run so far, so
 * it is the same every time and costs nothing. It never says anything the data
 * does not say, and it never guesses at how anybody felt.
 */
function readFor(e, running, run) {
  const who = run.roster.find((p) => p.id === e.playerId)?.label || '';
  const name = run.roster.length > 1 && who ? `${who}: ` : '';
  if (e.source === 'measured') {
    const kind = S.cfg?.measured?.[e.type]?.label || e.type;
    const el = elementFor(n0(e.value));
    const first = running.firstMeasured[e.type];
    const drift = first === undefined ? '' : ` That is ${e.value === first ? 'level with' : e.value > first ? `${e.value - first} above` : `${first - e.value} below`} the first one measured tonight.`;
    // An uncalibrated rep is a rough guide and the line says so rather than
    // reading out a number as if it had been checked against this player.
    const rough = e.metric && e.metric.calibrated === 0 ? ' Uncalibrated, so a rough guide.' : '';
    return `${name}${kind} measured ${e.value} out of 100. ${el.label}.${rough}${drift}`;
  }
  if (e.type === 'clutch_on') return 'Somebody marked this stretch clutch. That is their call, not a measurement.';
  if (e.type === 'clutch_off') return 'Clutch time marked over.';
  if (e.type === 'period') return 'New period.';
  const c = running.counts;
  const bump = (k) => n0(c[k]);
  const line = {
    fg3_made: () => `Three logged. ${bump('fg3_made')} of ${bump('fg3_made') + bump('fg3_miss')} from deep so far.`,
    fg3_miss: () => `Three missed. ${bump('fg3_made')} of ${bump('fg3_made') + bump('fg3_miss')} from deep so far.`,
    fg2_made: () => `Two logged. ${bump('fg2_made')} of ${bump('fg2_made') + bump('fg2_miss')} inside so far.`,
    fg2_miss: () => `Two missed. ${bump('fg2_made')} of ${bump('fg2_made') + bump('fg2_miss')} inside so far.`,
    ft_made: () => `Free throw logged. ${bump('ft_made')} of ${bump('ft_made') + bump('ft_miss')} on the night.`,
    ft_miss: () => `Free throw missed. ${bump('ft_made')} of ${bump('ft_made') + bump('ft_miss')} on the night.`,
    boxout: () => `Box out number ${bump('boxout')} logged.`,
    rebound_off: () => `Offensive board logged. ${bump('rebound_off') + bump('rebound_def')} rebounds so far.`,
    rebound_def: () => `Defensive board logged. ${bump('rebound_off') + bump('rebound_def')} rebounds so far.`,
    transition_back: () => `Got back on defense. ${bump('transition_back')} times logged.`,
    fastbreak: () => `Fast break logged. ${bump('fastbreak')} on the night.`,
    hustle: () => `Hustle play logged. ${bump('hustle')} so far.`,
    turnover: () => `Turnover logged. ${bump('turnover')} so far.`,
  }[e.type];
  return `${name}${line ? line() : `${typeLabel(e.type)} logged.`}`;
}

/* ---------------------------------------------------------------- events */

function onClick(ev) {
  const el = ev.target.closest('[data-act]');
  if (!el) return;
  const act = el.dataset.act;
  const i = Number(el.dataset.i);
  const s = S.setup;

  if (act === 'new') return openSetup();
  if (act === 'cancel') { dropPlayback(); S.screen = 'list'; S.run = null; render(); return; }
  if (act === 'open') return openRun(el.dataset.id);

  if (act === 'kind') { s.kind = el.dataset.id; paint(); return; }
  if (act === 'add') { s.roster.push({ id: rid('r'), label: '', accountId: null, tracked: false }); paint(); return; }
  if (act === 'drop') { s.roster.splice(i, 1); paint(); return; }
  if (act === 'track') { s.roster[i].tracked = !s.roster[i].tracked; paint(); return; }
  if (act === 'cam') { s.camera = !s.camera; paint(); return; }
  if (act === 'rec') { s.record = !s.record; paint(); return; }
  if (act === 'start') return startRun();

  if (act === 'who') { S.who = el.dataset.id; paint(); return; }
  if (act === 'tap') return logEvent(el.dataset.key, S.who);
  if (act === 'clutch') { S.clutch = !S.clutch; logEvent(S.clutch ? 'clutch_on' : 'clutch_off', null); return; }
  if (act === 'undo') return undo();
  if (act === 'mic') return mic();
  if (act === 'end') return endRun();

  if (act === 'self') {
    el.closest('.gmscale').querySelectorAll('.gmdot').forEach((d) => d.classList.toggle('on', Number(d.dataset.n) <= Number(el.dataset.n)));
    el.closest('.gmscale').dataset.value = el.dataset.n;
    return;
  }
  if (act === 'selfsave') return saveSelf();
  if (act === 'watch') return openPlayback();
  if (act === 'tocard') { dropPlayback(); S.screen = 'card'; paint(); return; }
  if (act === 'seek') {
    const e = S.play?.events?.[i];
    const v = document.getElementById('gmPlay');
    if (e && v) { v.currentTime = e.t / 1000; v.play().catch(() => {}); }
  }
}

function onChange(ev) {
  const el = ev.target.closest('[data-act]');
  if (!el || !S.setup) return;
  const i = Number(el.dataset.i);
  if (el.dataset.act === 'title') S.setup.title = el.value;
  if (el.dataset.act === 'label') S.setup.roster[i].label = el.value;
  if (el.dataset.act === 'number') S.setup.roster[i].number = el.value.replace(/\D/g, '').slice(0, 2) || null;
  if (el.dataset.act === 'link') {
    S.setup.roster[i].accountId = el.value || null;
    const person = S.people?.people?.find((x) => x.accountId === el.value);
    if (person && !S.setup.roster[i].label) S.setup.roster[i].label = person.label;
    paint();
  }
}

/* ------------------------------------------------------------------ flows */

async function openSetup() {
  S.setup = blankSetup();
  S.screen = 'setup';
  S.err = null;
  paint();
  // Who this account may link is a permission question and the Worker answers
  // it, so the picker never offers somebody the write would refuse.
  try { S.people = await ctx.api('/api/game/people'); } catch (e) { S.people = { people: [], reason: e.message }; }
  if (S.screen === 'setup') paint();
}

async function openRun(id) {
  try {
    const data = await ctx.api(`/api/game/${encodeURIComponent(id)}`);
    S.run = data.run;
    S.cfg = { kinds: data.kinds, logged: data.logged, markers: data.markers, measured: data.measured, copy: data.copy };
    S.screen = 'card';
    paint();
  } catch (e) { ctx.toast(e.message, true); }
}

async function startRun() {
  const s = S.setup;
  const roster = s.roster.map((p) => ({ label: p.label.trim() || 'Player', accountId: p.accountId || undefined }));
  const trackedIds = s.roster.map((p, i) => (p.tracked ? String(i) : null)).filter((x) => x !== null);
  try {
    S.run = await ctx.api('/api/game/start', { method: 'POST', body: JSON.stringify({ kind: s.kind, title: s.title, roster, trackedIds, clipId: s.record ? `c${Date.now()}` : undefined }) });
  } catch (e) { S.err = e.message; paint(); return; }

  S.tape = []; S.counts = {}; S.pending = []; S.live = null; S.clutch = false;
  S.who = S.run.trackedIds[0] || S.run.roster[0].id;
  S.wantCam = Boolean(s.camera || s.record);
  S.slots = []; S.jersey = {};
  S.cam.poseErr = null;
  S.screen = 'live';
  // t0 is taken here and moved to the frame the recorder starts on, below, so
  // the timeline and the video share one zero.
  S.t0 = performance.now();
  paint();
  tick();
  if (s.camera || s.record) await startStage(s);
  paint();
}

/** The clock in the header, once a second, so the sideline knows where it is. */
function tick() {
  clearInterval(S.ticking);
  S.ticking = setInterval(() => {
    const el = document.getElementById('gmClock');
    if (!el) return clearInterval(S.ticking);
    el.textContent = clock(performance.now() - S.t0);
  }, 1000);
}

const now = () => Math.max(0, Math.round(performance.now() - S.t0));

/**
 * One tap. It lands on the screen immediately and goes to the Worker in a
 * batch, because a phone on a sideline should never be waiting on a round trip.
 */
function logEvent(type, playerId, opts = {}) {
  if (!S.run || S.screen !== 'live') return;
  const marker = Boolean(S.cfg?.markers?.[type]);
  const who = marker ? null : (playerId || S.who || S.run.roster[0].id);
  const spot = opts.spot ?? (SHOT_TYPES.has(type) || type === 'spot' ? S.spot : undefined);
  const e = { localId: rid('l'), t: opts.t ?? now(), type, playerId: who, value: opts.value ?? 1, source: opts.source || 'logged', metric: opts.metric || undefined, spot: spot || undefined };
  S.tape.push(e);
  if (!marker && e.source === 'logged') {
    const c = (S.counts[who] ??= {});
    c[type] = n0(c[type]) + e.value;
  }
  S.pending.push(e);
  scheduleFlush();
  if (S.screen === 'live') paint();
}

function scheduleFlush() {
  clearTimeout(S.flushTimer);
  // Short enough that an undo after the flush is rare, long enough that a run
  // of six taps is one request.
  S.flushTimer = setTimeout(flush, 800);
}

async function flush() {
  if (S.flushing || !S.pending.length || !S.run) return;
  S.flushing = true;
  const batch = S.pending.splice(0, 60);
  try {
    const r = await ctx.api(`/api/game/${encodeURIComponent(S.run.id)}/event`, {
      method: 'POST',
      body: JSON.stringify({ events: batch.map(({ localId, ...rest }) => rest) }),
    });
    // added comes back in the order it was sent, so the local tape learns its
    // server ids and undo can reach an event that has already landed.
    r.added.forEach((srv, i) => { const local = S.tape.find((x) => x.localId === batch[i].localId); if (local) local.id = srv.id; });
    S.live = r.live;
    if (S.screen === 'live') paint();
  } catch (e) {
    S.pending.unshift(...batch);          // nothing is lost, it goes again
    ctx.toast(`Could not save that: ${e.message}`, true);
  } finally {
    S.flushing = false;
    if (S.pending.length) scheduleFlush();
  }
}

/** Put an undone event back, exactly as it was, when the undo could not land. */
function restore(e) {
  S.tape.push(e);
  if (!S.cfg?.markers?.[e.type] && e.source === 'logged') {
    const c = (S.counts[e.playerId] ??= {});
    c[e.type] = n0(c[e.type]) + e.value;
  }
  if (e.type === 'clutch_on') S.clutch = true;
  if (e.type === 'clutch_off') S.clutch = false;
  paint();
}

async function undo() {
  const e = S.tape[S.tape.length - 1];
  if (!e) return;
  const waiting = S.pending.findIndex((x) => x.localId === e.localId);
  // Neither waiting to be sent nor acknowledged means it is in flight. Nothing
  // is changed at all in that case: taking it off the screen and putting it
  // back is how an undo ends up half applied.
  if (waiting < 0 && !e.id) { ctx.toast('That one is still saving. Try the undo again in a second.', true); return; }

  S.tape.pop();
  if (!S.cfg?.markers?.[e.type] && e.source === 'logged') {
    const c = S.counts[e.playerId] || {};
    c[e.type] = Math.max(0, n0(c[e.type]) - e.value);
  }
  if (e.type === 'clutch_on') S.clutch = false;
  if (e.type === 'clutch_off') S.clutch = true;
  if (waiting >= 0) { S.pending.splice(waiting, 1); paint(); return; }
  paint();
  try {
    const r = await ctx.api(`/api/game/${encodeURIComponent(S.run.id)}/event/${encodeURIComponent(e.id)}`, { method: 'DELETE' });
    S.live = r.live;
    paint();
  } catch (err) {
    // The Worker still has it, so the screen goes back to agreeing with it.
    ctx.toast(err.message, true);
    restore(e);
  }
}

/**
 * Voice logging. The app already has speech recognition in public/js/app.js
 * listen(), and it is not rebuilt here. What this module owns is the phrase
 * parser: voiceLine(text) turns "kobe made a three" into the right event
 * against the right player. app.js only has to hand the transcript over.
 */
function mic() {
  if (typeof ctx.listen === 'function') {
    const wasOn = ctx.listening?.();
    ctx.listen();
    // The microphone stays open across the whole run, through the quiet
    // stretches between plays, so a call of "corner three, good" lands without
    // anybody touching the phone.
    if (!wasOn) ctx.toast('Listening. Call out your plays and they land on the sheet.');
    paint();
    return;
  }
  ctx.toast('Voice logging needs a microphone. Tapping works now.', true);
}

/** Exported so public/js/app.js can route a transcript here while a run is live. */
export function voiceLine(text) {
  const said = String(text || '').toLowerCase().trim();
  if (!said || !S.run || S.screen !== 'live') return false;
  const t = now();                                   // the moment they said it, not the moment it parsed
  // A name on the roster, said out loud, moves the sheet to that player first.
  const named = S.run.roster.find((p) => p.label && said.includes(p.label.toLowerCase()));
  if (named) S.who = named.id;
  // Where they are comes first, so a sentence can move the spot and log a
  // shot from it in one breath.
  const spotHit = SPOT_PHRASES.find(([re]) => re.test(said));
  if (spotHit) S.spot = spotHit[1];

  const moveHit = MOVE_PHRASES.find(([re]) => re.test(said));
  if (moveHit) logEvent(moveHit[1], named?.id || S.who, { t });

  const hit = PHRASES.find(([re]) => re.test(said));
  if (!hit) {
    if (moveHit) { ctx.toast(`${typeLabel(moveHit[1])}, logged.`); return true; }
    // A spot on its own is a real thing to say. "I am at the corner."
    if (spotHit) { logEvent('spot', null, { t, spot: S.spot }); ctx.toast(`Shooting from ${spotName(S.spot)}.`); paint(); return true; }
    ctx.toast(`Did not catch an action in "${text}".`, true); return false;
  }
  const type = hit[1];
  if (type === 'clutch_on') S.clutch = true;
  if (type === 'clutch_off') S.clutch = false;
  logEvent(type, named?.id || S.who, { t });
  const where = SHOT_TYPES.has(type) && S.spot ? ` from ${spotName(S.spot)}` : '';
  const withMove = moveHit ? `${typeLabel(moveHit[1])} into ` : '';
  ctx.toast(`${withMove}${typeLabel(type)}${where}${named ? ` for ${named.label}` : ''}, logged.`);
  return true;
}

/** True while a run is live, so app.js knows to send the transcript here. */
export const wantsVoice = () => S.screen === 'live' && Boolean(S.run);

async function endRun() {
  const go = await ctx.ui.confirm({ title: 'End this run?', body: 'The card is written and the recording is saved to your Vault.', confirmText: 'End it' });
  if (!go) return;
  clearInterval(S.ticking);
  const durationMs = now();
  await flush();
  const clipId = await stopStage(durationMs);
  // Stopping the stage flushes whatever the pose layer was still holding, and
  // those reps are events like any other. They go before the settle, not after.
  await flush();
  try {
    const r = await ctx.api(`/api/game/${encodeURIComponent(S.run.id)}/end`, { method: 'POST', body: JSON.stringify({ durationMs, clipId }) });
    S.run = { ...r.run, filed: r.filed, reportError: r.reportError };
    S.screen = 'card';
    paint();
    ctx.refresh?.journal?.();
  } catch (e) {
    ctx.toast(e.message, true);
    S.screen = 'list';
    render();
  }
}

async function saveSelf() {
  const read = (k) => { const el = body.querySelector(`.gmscale[data-value] .gmdot[data-k="${k}"]`)?.closest('.gmscale'); return el ? Number(el.dataset.value) : null; };
  const selfCheck = {
    legs: read('legs'), focus: read('focus'), energy: read('energy'),
    note: body.querySelector('[data-act="selfnote"]')?.value || '',
  };
  try {
    const r = await ctx.api(`/api/game/${encodeURIComponent(S.run.id)}/end`, { method: 'POST', body: JSON.stringify({ selfCheck }) });
    S.run = { ...r.run, filed: r.filed };
    paint();
  } catch (e) { ctx.toast(e.message, true); }
}

/* ------------------------------------------------------- camera and pose */

/**
 * The stage: camera, pose, the recording. All three are optional and a failure
 * in any of them leaves the tap sheet working, because the tap sheet is the
 * part somebody is standing on a sideline relying on.
 *
 * public/js/posemetrics.js is the pose layer and this module only ever asks it
 * for one thing:
 *     createTracker({ onRep }) -> { push(landmarks, tMs, poseIndex), reset() }
 * onRep is called with { type, score, metrics } where type is one of the
 * measured types the Worker knows. If that module is not there yet, the
 * shooting form detector already in public/scoring.js stands in, so a run still
 * measures something real.
 */
async function startStage(s) {
  const video = document.getElementById('gmCam');
  const overlay = document.getElementById('gmOverlay');
  if (!video) return;
  try {
    S.cam.stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 960 }, facingMode: 'user' }, audio: false });
    video.srcObject = S.cam.stream;
    await video.play();
    S.cam.on = true;
  } catch (e) {
    S.cam.poseErr = e.name === 'NotAllowedError' ? 'Camera permission was denied. Tapping still works.' : `Camera did not start: ${e.message}. Tapping still works.`;
    return;
  }

  if (s.record && 'MediaRecorder' in window) {
    try {
      S.cam.recorder = vault.startRecording(video, overlay);
      // The recorder is running as of this line, so this is the zero the whole
      // timeline is measured from and the zero the playback video shares.
      S.t0 = performance.now();
    } catch (e) { console.warn('recording unavailable', e); S.cam.recorder = null; }
  }

  if (!s.camera) return;
  try {
    const tracked = S.run.trackedIds.length || 1;
    // The pose model runs in the tracking worker (public/js/track-worker.js),
    // off the page's thread, so the tap buttons and the clock never wait on
    // it; on the page only where a worker cannot run it.
    S.cam.client = createTrackClient({
      visionUrl: VISION_URL, loadVision: () => import(VISION_URL), model: 'lite',
      // Up to ten, for five on five. Asking for more bodies costs frame rate on
      // every frame whether or not they are there, so it follows the roster: a
      // two player workout is not made to pay for a full court it is not using.
      numPoses: Math.min(10, Math.max(1, tracked)), hands: false,
      onResult: onPoses,
    });
    await S.cam.client.ready;
  } catch (e) {
    S.cam.client?.close(); S.cam.client = null;
    S.cam.poseErr = 'The pose model did not load, so no form was measured. Tapping still works.';
    return;
  }

  S.cam.tracker = await makeTracker();
  loop();
}

/** What public/scoring.js calls a rep, to what the Worker calls an event type. */
const KIND_TO_TYPE = {
  shot: 'form_shot', dribble: 'dribble', balance: 'balance', stance: 'stance', boxout: 'boxout_form', drive: 'drive',
  pivot: 'pivot', slide: 'slide', footwork: 'footwork',
};

/**
 * Which player a measured rep belongs to.
 *
 * The app cannot tell who a body is. The pose panel is where somebody names a
 * body, and it hands that name back on the rep, so a named body goes to the
 * roster entry with that name. Otherwise the body's own slot picks a tracked
 * player, and if that fails the rep goes to the first player being tracked.
 * None of this is recognition and nothing here claims it is.
 */
function playerForRep(rep) {
  const tracked = S.run.trackedIds;
  const said = String(rep?.player || '').trim().toLowerCase();
  const named = said && S.run.roster.find((p) => String(p.label).trim().toLowerCase() === said);
  if (named && tracked.includes(named.id)) return named.id;
  // A slot number is the pose layer's own id for a body and it does not start
  // at zero or line up with a roster, so bodies are matched in the order they
  // first appear rather than by the number itself.
  const slot = Number(rep?.slot);
  if (Number.isFinite(slot)) {
    if (!S.slots.includes(slot)) S.slots.push(slot);
    const byOrder = tracked[S.slots.indexOf(slot)];
    if (byOrder) return byOrder;
  }
  return tracked[0];
}

/** The raw numbers behind a score, so the card and the playback can explain it. */
function repMetrics(rep) {
  const out = { calibrated: rep?.calibrated ? 1 : 0 };
  for (const [k, v] of Object.entries(rep?.metrics || {})) if (Number.isFinite(Number(v))) out[k] = Number(v);
  return out;
}

async function makeTracker() {
  const take = (rep) => {
    const type = KIND_TO_TYPE[rep?.kind];
    if (!type || !S.cfg?.measured?.[type]) return;
    logEvent(type, playerForRep(rep), { value: Math.round(n0(rep.overall)), source: 'measured', metric: repMetrics(rep) });
  };
  try {
    const pm = await import('./posemetrics.js');
    if (typeof pm.onFrame === 'function' && typeof pm.onRep === 'function') {
      const off = pm.onRep(take);
      pm.start?.();
      let probed = false;
      return {
        nameOf: (slot) => (typeof pm.nameOf === 'function' ? pm.nameOf(slot) : `Body ${slot}`),
        label: (slot, name) => { if (typeof pm.label === 'function') pm.label(slot, name); },
        push(poses, tMs, opts) {
          const r = pm.onFrame(poses, tMs, opts);
          // onFrame answers null when that module was never mounted, which
          // happens if app.js has not been wired to it yet. Mount it once
          // rather than running a whole game measuring nothing.
          if (r === null && !probed) { probed = true; try { pm.mount?.(ctx); pm.start?.(); } catch (e) { console.warn('pose panel', e); } }
          return r;
        },
        // Flush BEFORE unsubscribing: reps still being held when a run ends
        // are emitted by pm.stop(), and off() first threw them away.
        stop() { let out = null; try { out = pm.stop?.(); } catch { out = null; } off(); return out; },
      };
    }
  } catch { /* the pose layer is not in this build */ }
  // Fallback: the shooting form detector in public/scoring.js, on the first
  // body only. One detector where the pose layer has six, and the screen says
  // so rather than pretending the rest were measured and came back empty.
  const { createShotDetector } = await import('../scoring.js');
  const det = createShotDetector();
  return {
    push(poses, tMs) {
      const lm = poses?.[0];
      if (!lm) return;
      const shot = det.push(lm, tMs);
      if (shot) take({ kind: 'shot', overall: shot.overall, metrics: shot.metrics, slot: 0 });
    },
    stop() { det.reset(); return null; },
    partial: true,
  };
}

const BONES = [[11, 12], [11, 23], [12, 24], [23, 24], [11, 13], [13, 15], [12, 14], [14, 16],
  [23, 25], [25, 27], [24, 26], [26, 28], [27, 29], [27, 31], [29, 31], [28, 30], [28, 32], [30, 32]];

/**
 * Hands each new camera frame to the tracking worker, one at a time: while a
 * frame is being read, newer ones are skipped, never queued, so a phone that
 * cannot keep up falls to a lower frame rate (frameRate says so) instead of
 * falling behind the game.
 */
function loop() {
  if (!S.cam.on || S.screen !== 'live') return;
  const video = document.getElementById('gmCam');
  const c = S.cam.client;
  if (video && c && !c.busy && video.readyState >= 2 && video.currentTime !== loop.last) {
    loop.last = video.currentTime;
    loop.n = (loop.n || 0) + 1;
    // Shirt colours only matter with more than one body in view, and a few
    // readings a second are plenty for the tracker to blend.
    c.submit(video, performance.now(), { pose: true, hands: false, looks: (S.cam.bodies || 0) > 1 && loop.n % 3 === 0 });
  }
  S.cam.raf = requestAnimationFrame(loop);
}

/** The bodies in one frame, back from the tracking worker. */
function onPoses(r) {
  if (!S.cam.on || S.screen !== 'live' || !r.poses) return;
  const video = document.getElementById('gmCam');
  const overlay = document.getElementById('gmOverlay');
  if (!video || !overlay) return;
  try {
    const poses = r.poses;
    S.cam.bodies = poses.length;
    // t is run time, not wall time, so a measured rep sits on the timeline
    // exactly where a tap does, taken from the moment the frame was captured,
    // not the moment its answer came back. Every body goes in together: the
    // pose layer is what decides which body is which across frames, not this loop.
    const tracked = S.cam.tracker?.push(poses, Math.max(0, Math.round(r.t - S.t0)), { looks: r.looks || null });
    if (tracked?.people?.length) lockByJersey(video, tracked.people);
    // Drawn from what the tracker decided, so a skeleton keeps its colour and
    // its name for as long as it is the same player. It used to be drawn from
    // the raw list, coloured by position in that list, and the model hands
    // bodies back in no fixed order: the colours hopped between players from
    // one frame to the next even when the tracking underneath was right.
    draw(overlay, video, tracked?.people || poses.map((lm, i) => ({ slot: i + 1, lm })));
    frameRate.tick(poses.length);
  } catch (e) { /* one bad frame is not a reason to stop the run */ }
}

/**
 * Is the phone keeping up? Ten bodies is a lot to ask of one, and a tracker
 * that has quietly dropped to four frames a second misses reps and loses people
 * without a word. So the rate is measured, and when it is too low for the
 * number of players in view the screen says so, in plain words, with the two
 * things that actually fix it. It never silently tracks fewer people than it
 * was asked to: a stat that is missing should be known to be missing.
 */
const frameRate = (() => {
  let stamps = [], warnedAt = 0;
  return {
    reset() { stamps = []; warnedAt = 0; },
    tick(bodies) {
      const t = performance.now();
      stamps.push(t);
      while (stamps.length && t - stamps[0] > 3000) stamps.shift();
      if (stamps.length < 6 || t - stamps[0] < 2500) return;
      const fps = (stamps.length - 1) / ((t - stamps[0]) / 1000);
      S.cam.fps = Math.round(fps);
      if (fps < 8 && bodies > 1 && t - warnedAt > 45000) {
        warnedAt = t;
        ctx.toast(`Your device is slow with ${bodies} players. Move the camera closer, or track fewer.`, true);
      }
    },
  };
})();

/* -------------------------------------------------------- lock by jersey
 * With numbers on the roster, each body the tracker hands over gets its
 * torso cropped and read once by the vision model; a number that matches a
 * tracked player names that body, and from then on its reps file to that
 * player and its skeleton wears the name. The lock is the number on the
 * shirt, never the face. A body too small to read, or with no number in
 * view, is tried again a few times a few seconds apart and then left as
 * "Body N", which is what the app actually knows. A new slot (a player who
 * left the frame and came back) is read again, so the lock survives that.
 */
const JERSEY = { RETRY_MS: 4000, MAX_TRIES: 6, MIN_SHOULDER: 0.06, CROP_W: 224 };
let jerseyCanvas = null;
function torsoCrop(video, lm) {
  const W = video.videoWidth, H = video.videoHeight;
  const pts = [11, 12, 23, 24].map((i) => lm[i]);
  const xs = pts.map((p) => p.x * W), ys = pts.map((p) => p.y * H);
  const bw = Math.max(...xs) - Math.min(...xs), bh = Math.max(...ys) - Math.min(...ys);
  const x0 = Math.max(0, Math.min(...xs) - bw * 0.3), x1 = Math.min(W, Math.max(...xs) + bw * 0.3);
  const y0 = Math.max(0, Math.min(...ys) - bh * 0.2), y1 = Math.min(H, Math.max(...ys) + bh * 0.1);
  const sw = Math.max(8, x1 - x0), sh = Math.max(8, y1 - y0);
  const cw = JERSEY.CROP_W, ch = Math.round(cw * sh / sw);
  jerseyCanvas ??= document.createElement('canvas');
  jerseyCanvas.width = cw; jerseyCanvas.height = ch;
  jerseyCanvas.getContext('2d').drawImage(video, x0, y0, sw, sh, 0, 0, cw, ch);
  return jerseyCanvas.toDataURL('image/jpeg', 0.8);
}
async function lockByJersey(video, people) {
  const run = S.run; if (!run || !ctx) return;
  const numbered = run.roster.filter((p) => p.number && run.trackedIds.includes(p.id));
  if (!numbered.length) return;
  for (const person of people) {
    const st = (S.jersey[person.slot] ??= { tries: 0, at: 0, busy: false, done: false, reads: [] });
    if (st.done || st.busy || st.tries >= JERSEY.MAX_TRIES || performance.now() - st.at < JERSEY.RETRY_MS) continue;
    const lm = person.lm; const a = lm?.[11], b = lm?.[12];
    if (!a || !b || !lm[23] || !lm[24] || Math.hypot(a.x - b.x, a.y - b.y) < JERSEY.MIN_SHOULDER) continue;
    st.busy = true; st.tries++; st.at = performance.now();
    try {
      const r = await ctx.api('/api/vision/jersey', { method: 'POST', body: JSON.stringify({ image: torsoCrop(video, lm) }) });
      st.reads.push(r?.number ?? null);
      // A vision model asked for a digit will sometimes hand one back for a
      // blank shirt. One read is a hint; two reads seconds apart that agree,
      // on a number that is on the roster, is a lock.
      const [a, b] = st.reads.slice(-2);
      const hit = a && a === b ? numbered.find((p) => String(p.number) === String(a)) : null;
      if (hit) {
        st.done = true;
        S.cam.tracker?.label?.(person.slot, hit.label);
        ctx.toast?.(`Number ${hit.number} is ${hit.label}. Locked by the jersey, not the face.`);
      }
    } catch { /* the next try is a few seconds away */ }
    finally { st.busy = false; }
  }
}

function draw(cv, video, people) {
  const w = video.videoWidth || 1280, h = video.videoHeight || 720;
  if (cv.width !== w) { cv.width = w; cv.height = h; }
  const g = cv.getContext('2d');
  g.clearRect(0, 0, w, h);
  // Ten, one per slot, picked to stay apart from each other on a court.
  const COLORS = ['#ff5a1f', '#2ea8ff', '#9d6bff', '#22d97a', '#ffd23f', '#ff4fa3', '#2fe0d0', '#ff8a3d', '#8fb4ff', '#b6f05a'];
  const many = people.length;
  people.forEach((person) => {
    const lm = person.lm, i = person.slot - 1;
    const color = COLORS[((i % COLORS.length) + COLORS.length) % COLORS.length];
    const X = (p) => (1 - p.x) * w;                  // mirrored, to match what they see
    const Y = (p) => p.y * h;
    g.strokeStyle = color; g.lineWidth = 4; g.lineCap = 'round';
    for (const [a, b] of BONES) {
      if (!lm[a] || !lm[b]) continue;
      g.beginPath(); g.moveTo(X(lm[a]), Y(lm[a])); g.lineTo(X(lm[b]), Y(lm[b])); g.stroke();
    }
    g.fillStyle = color;
    for (const p of lm) { g.beginPath(); g.arc(X(p), Y(p), 3.5, 0, Math.PI * 2); g.fill(); }
    // The app cannot tell who a pose belongs to, and the order MediaPipe hands
    // back its poses is not stable from one frame to the next. So a body only
    // wears a player's name when there is nothing to get wrong: one body in
    // frame and one player being tracked. Any other time it is "Pose 1", which
    // is what the app actually knows.
    const head = lm[0];
    if (head) {
      const solo = many === 1 && S.run.trackedIds.length === 1;
      // The name somebody gave this box, or "Body 3". With one player and one
      // body there is nothing to get wrong, so it wears the player's name.
      const name = solo
        ? (S.run.roster.find((r) => r.id === S.run.trackedIds[0])?.label || 'Body 1')
        : (S.cam.tracker?.nameOf?.(person.slot) || `Body ${person.slot}`);
      g.font = '700 26px Nunito, sans-serif'; g.fillStyle = color;
      g.fillText(name, X(head) - 30, Y(head) - 26);
    }
  });
}

/** Repaints replace the video element, so the stream is put back on the new one. */
function attachStage() {
  const video = document.getElementById('gmCam');
  if (video && S.cam.stream && video.srcObject !== S.cam.stream) {
    video.srcObject = S.cam.stream;
    video.play().catch(() => {});
  }
}

async function stopStage(durationMs) {
  cancelAnimationFrame(S.cam.raf);
  // Anything the pose layer was still holding is flushed before the run
  // settles, so a rep in progress at the final whistle still counts.
  try { S.cam.tracker?.stop?.(); } catch (e) { console.warn('pose stop', e); }
  let clipId = null;
  if (S.cam.recorder) {
    const r = S.cam.recorder; S.cam.recorder = null;
    try {
      const { blob, duration, poster } = await r.stop();
      if (blob.size > 50_000) {
        clipId = S.run.clip?.id || `c${Date.now()}`;
        await vault.saveClip({
          id: clipId, runId: S.run.id, at: new Date().toISOString(), size: blob.size,
          duration: duration || durationMs / 1000, poster, blob,
          attempts: 0, makes: 0, form: null, companion: ctx.S?.settings?.companion || 'basketball',
        });
        ctx.toast(`Run saved to your Vault (${vault.fmtBytes(blob.size)}).`);
      }
    } catch (e) { ctx.toast(`Could not save the recording: ${e.message}`, true); }
  }
  S.cam.stream?.getTracks().forEach((t) => t.stop());
  S.cam.client?.close();
  S.cam.stream = null; S.cam.on = false; S.cam.client = null; S.cam.tracker = null;
  return clipId;
}

/* -------------------------------------------------------- playback wiring */

/** Let go of the recording. A blob url the browser still holds is the whole clip. */
function dropPlayback() {
  if (S.play?.url) { try { URL.revokeObjectURL(S.play.url); } catch {} }
  S.play = null;
}

async function openPlayback() {
  dropPlayback();
  const run = S.run;
  S.play = { run, events: (run.events || []).slice().sort((a, b) => a.t - b.t), err: null, url: null, i: 0 };
  if (!S.play.events.length) {
    try {
      const data = await ctx.api(`/api/game/${encodeURIComponent(run.id)}`);
      S.play.run = data.run;
      S.play.events = (data.run.events || []).slice().sort((a, b) => a.t - b.t);
    } catch (e) { S.play.err = e.message; }
  }
  try {
    const clip = run.clip ? await vault.getClip(run.clip.id) : null;
    if (clip?.blob) S.play.url = URL.createObjectURL(clip.blob);
    else S.play.err = 'That recording is not on this device. Recordings stay on the device that made them unless you save one to the cloud.';
  } catch (e) { S.play.err = `Could not open the recording: ${e.message}`; }
  S.screen = 'play';
  paint();
}

/**
 * The synchronized part. One timeupdate handler walks the timeline forward and
 * backward, so scrubbing is as correct as playing.
 */
function attachPlayer() {
  const p = S.play;
  const v = document.getElementById('gmPlay');
  const feed = document.getElementById('gmFeed');
  const read = document.getElementById('gmRead');
  const head = document.getElementById('gmHead');
  if (!v || !p) return;
  if (p.url) v.src = p.url;

  const dur = n0(p.run.durationMs) || 1;
  const running = { counts: {}, firstMeasured: {} };
  let at = -1;

  const rebuild = (tNow) => {
    // Backward is a rebuild rather than an unwind: it is a few hundred events
    // and it is the only way a scrub lands on the same state as a play.
    running.counts = {}; running.firstMeasured = {};
    feed.innerHTML = '';
    at = -1;
    for (let i = 0; i < p.events.length && p.events[i].t <= tNow; i++) step(i);
  };

  const step = (i) => {
    const e = p.events[i];
    if (e.source === 'logged') running.counts[e.type] = n0(running.counts[e.type]) + n0(e.value);
    const line = readFor(e, running, p.run);
    // Recorded AFTER the line is built, so the first rep of a kind does not get
    // told it is level with itself.
    if (e.source === 'measured' && running.firstMeasured[e.type] === undefined) running.firstMeasured[e.type] = e.value;
    const row = document.createElement('div');
    row.className = `gmfe ${e.source}`;
    row.innerHTML = `<span class="gmfet">${clock(e.t)}</span><span>${esc(line)}</span>`;
    feed.appendChild(row);
    feed.scrollTop = feed.scrollHeight;
    if (read) read.textContent = line;
    at = i;
  };

  v.ontimeupdate = () => {
    const tNow = v.currentTime * 1000;
    if (head) head.style.left = `${Math.min(100, (tNow / dur) * 100)}%`;
    if (at >= 0 && p.events[at] && p.events[at].t > tNow) return rebuild(tNow);
    while (at + 1 < p.events.length && p.events[at + 1].t <= tNow) step(at + 1);
  };
  v.onseeking = () => rebuild(v.currentTime * 1000);
  v.onended = () => { if (read && p.run.report) read.textContent = p.run.report; };
}
